import { StepperMessage } from '../../../common/enums/message.enum';
import { FlowTypes } from '../../../common/models/order/order.model';
import { OrderRoutes } from '../orders-routes.enum';
import { FlowContext } from './flow.types';
import { StepperChildDefinition, StepperFlowDefinition, StepperSectionDefinition } from './stepper.types';

// Stepper grouping of each product flow's pages (story 10869). The Next/Back tables in *.flow.ts decide
// where buttons go; these definitions only decide how reached and upcoming pages are grouped and shown.
// A page that only appears for some orders is listed with `ifVisited`, so it shows once the agent reaches it.

const ifVisited = (): boolean => false;

const section = (id: string, label: StepperMessage, children: StepperChildDefinition[]): StepperSectionDefinition =>
    ({ id, label, children });

const creditOutcomePages: StepperChildDefinition[] = [
    { route: OrderRoutes.PREVIOUS_ADDRESS_COLLECTION, when: ifVisited },
    { route: OrderRoutes.IDENTITY_VERIFICATION, when: ifVisited },
    { route: OrderRoutes.NCVC_QUESTIONNAIRE, when: ifVisited },
    { route: OrderRoutes.ACCOUNT_REVIEW, when: ifVisited },
    { route: OrderRoutes.OUTSTANDING_BALANCE, when: ifVisited }
];

const securityRequired = (ctx: FlowContext): boolean => ctx.isSecurityAnswerRequired() || ctx.isSecurityPinRequired();

/** Shared pages finished before any product flow starts; every flow lists them first under Programming. */
const preFlowPages: StepperChildDefinition[] = [
    { route: OrderRoutes.ORDER_SETUP, doneOnEntry: true },
    { route: OrderRoutes.SERVICE_INFORMATION, doneOnEntry: true }
];

const programming = (children: StepperChildDefinition[]) =>
    section('programming', StepperMessage.Programming, [...preFlowPages, ...children]);
const hardware = (children: StepperChildDefinition[]) => section('hardware', StepperMessage.Hardware, children);
const configuration = (children: StepperChildDefinition[]) => section('configuration', StepperMessage.Configuration, children);
const creditCheck = (children: StepperChildDefinition[]) => section('credit-check', StepperMessage.CreditCheck, children);
const offers = (children: StepperChildDefinition[]) => section('offers', StepperMessage.Offers, children);
const options = (children: StepperChildDefinition[]) => section('options', StepperMessage.Options, children);
const summary = () => section('summary', StepperMessage.Summary, [{ route: OrderRoutes.PRODUCT_SUMMARY }]);

export const INTERNET_STEPPER: StepperFlowDefinition = () => [
    programming([{ route: OrderRoutes.INTERNET_PACKAGE_SELECTION }]),
    creditCheck([
        { route: OrderRoutes.CREDIT_CHECK, when: ctx => !ctx.canReuseQualifiedSelections() },
        ...creditOutcomePages,
        { route: OrderRoutes.ADVANCED_BILL_PAYMENT, when: ifVisited }
    ]),
    offers([
        { route: OrderRoutes.ADDITIONAL_INTERNET_OFFERS },
        { route: OrderRoutes.CLOSING_OFFERS, when: ctx => ctx.internetClosingOffersAvailable() }
    ]),
    options([
        { route: OrderRoutes.ALTERNATE_SHIPPING, when: ctx => ctx.allowShippingAddressChange() },
        { route: OrderRoutes.CUSTOMER_ACCOUNT_SETUP, when: securityRequired },
        { route: OrderRoutes.WINDSTREAM_CUSTOMER_ACCOUNT_SETUP, when: ifVisited }
    ]),
    summary()
];

export const VIDEO_STEPPER: StepperFlowDefinition = ctx => {
    const satellite = !!ctx.dtvSatellitePackageSelected();
    const genre = !!ctx.genrePackageSelected();
    const residential = !ctx.isCommercial();

    const hardwareSection = hardware(satellite
        ? [
            { route: OrderRoutes.SATELLITE_HARDWARE_SELECTION },
            { route: OrderRoutes.DTV_VIDEO_COMMERCIAL_SATELLITE_OFFERS, when: ifVisited }
        ]
        : [{ route: OrderRoutes.STREAM_HARDWARE_SELECTION }]);

    // Satellite always runs credit for residential orders; Genre unless UO says it isn't required; Stream only when the hardware page asks.
    const creditSection = creditCheck([
        { route: OrderRoutes.CREDIT_CHECK, when: c => residential && (satellite || (genre && c.creditCheckRequired())) },
        ...creditOutcomePages,
        { route: OrderRoutes.CREDIT_RESULTS, when: ifVisited }
    ]);

    const offersSection = offers([
        { route: OrderRoutes.STREAM_GENRE_PACK, when: () => genre },
        { route: OrderRoutes.ADDITIONAL_VIDEO_OFFERS },
        { route: OrderRoutes.ADDITIONAL_CHANNEL_OFFERS },
        { route: OrderRoutes.CLOSING_OFFERS, when: c => c.closingOffersAvailable() }
    ]);

    const optionsSection = options([
        { route: OrderRoutes.EMAIL_VALIDATION, when: c => !satellite && residential && !c.hasMyFreeResidentialStreamAccount() },
        {
            route: OrderRoutes.ALTERNATE_SHIPPING,
            when: c => c.allowShippingAddressChange() || c.isCommercial() || c.hasMyFreeResidentialStreamAccount()
        },
        { route: OrderRoutes.CUSTOMER_ACCOUNT_SETUP, when: ifVisited }
    ]);

    const programmingSection = programming([{ route: OrderRoutes.VIDEO_PACKAGE_SELECTION }]);

    // Genre runs credit after the offers pages.
    return genre
        ? [programmingSection, hardwareSection, offersSection, creditSection, optionsSection, summary()]
        : [programmingSection, hardwareSection, creditSection, offersSection, optionsSection, summary()];
};

export const WIRELESS_STEPPER: StepperFlowDefinition = () => [
    programming([
        { route: OrderRoutes.WIRELESS_SETUP },
        { route: OrderRoutes.BUSINESS_VERIFICATION, when: ctx => ctx.isNewWirelessCustomer() && ctx.orderType() === 'smbnew' }
    ]),
    creditCheck([
        { route: OrderRoutes.CREDIT_CHECK },
        ...creditOutcomePages,
        { route: OrderRoutes.WIRELESS_ID_AUTHENTICATION, when: ifVisited },
        { route: OrderRoutes.MOBILE_BUSINESS_AGREEMENT, when: ifVisited },
        { route: OrderRoutes.SIGFAN, when: ifVisited }
    ]),
    configuration([{ route: OrderRoutes.LINE_CONFIGURATION }]),
    offers([{ route: OrderRoutes.ADDITIONAL_WIRELESS_OFFERS, when: ifVisited }]),
    options([
        { route: OrderRoutes.ALTERNATE_SHIPPING, when: ifVisited },
        { route: OrderRoutes.SERVICE_LEVEL_SELECTION, when: ifVisited },
        { route: OrderRoutes.CUSTOMER_ACCOUNT_SETUP, when: ifVisited }
    ]),
    summary()
];

export const VOICE_STEPPER: StepperFlowDefinition = () => [
    programming([{ route: OrderRoutes.VOICE_PACKAGE_SELECTION }]),
    configuration([{ route: OrderRoutes.VOICE_CONFIGURATION }]),
    offers([{ route: OrderRoutes.ADDITIONAL_VOICE_OFFERS, when: ifVisited }]),
    options([{ route: OrderRoutes.CUSTOMER_ACCOUNT_SETUP, when: ifVisited }]),
    summary()
];

/** Before a product is picked (Order Setup, Service Information) only Programming is known. */
export const PRE_FLOW_STEPPER: StepperFlowDefinition = () => [programming([])];

export const STEPPER_FLOWS: Record<string, StepperFlowDefinition> = {
    [FlowTypes.INTERNET]: INTERNET_STEPPER,
    [FlowTypes.VIDEO]: VIDEO_STEPPER,
    [FlowTypes.WIRELESS]: WIRELESS_STEPPER,
    [FlowTypes.VOICE]: VOICE_STEPPER
};
